import { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { toast } from "sonner";
import { X, KeyRound } from "lucide-react";

export default function ChangePasswordDialog({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("New password must be at least 6 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed");
      onClose?.();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="change-password-dialog">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-[#15233B]/10 bg-[#F6F3EC] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-[#FBBF24]" />
            <h2 className="text-lg font-extrabold tracking-tight text-[#15233B]">Change password</h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded hover:bg-[#15233B]/5">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Current password</label>
            <input
              data-testid="cp-current"
              type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
              className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white" required
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">New password</label>
            <input
              data-testid="cp-new"
              type="password" value={next} onChange={(e) => setNext(e.target.value)}
              className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white" required minLength={6}
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Confirm new password</label>
            <input
              data-testid="cp-confirm"
              type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white" required minLength={6}
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#15233B]/10 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 rounded text-sm font-semibold text-[#15233B] hover:bg-[#15233B]/5">Cancel</button>
          <button data-testid="cp-submit" type="submit" disabled={busy} className="px-4 py-2 rounded bg-[#FBBF24] text-white text-sm font-bold hover:bg-[#F59E0B] disabled:opacity-60">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
